import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { Map as MapLibreMap, Marker, Popup } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import TopNavBar from '../../components/TopNavBar/TopNavBar';
import styles from './BirdMap.module.css';

type Hotspot = {
  locId: string;
  locName: string;
  lat: number;
  lng: number;
  countryCode?: string;
  subnational1Code?: string;
  subnational2Code?: string;
};

type Observation = {
  comName: string;
  sciName?: string;
  obsDt?: string;
  howMany?: number;
  locId?: string;
  locName?: string;
  lat?: number;
  lng?: number;
};

const buildFieldGuideLink = (name: string) => {
  // All About Birds uses underscores between words for direct guide pages
  const slug = name.trim().replace(/\s+/g, '_');
  return `https://www.allaboutbirds.org/guide/${encodeURIComponent(slug)}`;
};

const EBIRD_TOKEN = process.env.REACT_APP_EBIRD_TOKEN;
// Carto Voyager GL (stable)
const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json';
const DEFAULT_CENTER: [number, number] = [-75.37, 40.04]; // near Philly
const DEFAULT_ZOOM = 8;
const DEFAULT_RADIUS_MILES = 15; // convert to km for API
const DEFAULT_BACK_DAYS = 14;
const US_BOUNDS = { minLat: 24.0, maxLat: 50.0, minLng: -125.0, maxLng: -66.5 };

const curatedSites: Hotspot[] = [
  { locId: 'L144860', locName: 'John James Audubon Center', lat: 40.123349, lng: -75.4421161 },
  { locId: 'L504403', locName: 'John Heinz NWR', lat: 39.8893501, lng: -75.2601457 },
  // Add Seattle/PNW sites here when available
];

async function fetchJson<T>(url: string, token?: string): Promise<T> {
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      ...(token ? { 'X-eBirdApiToken': token } : {}),
    },
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`Request failed (${res.status}): ${text.slice(0, 200)}`);
  }
  try {
    return JSON.parse(text);
  } catch (err) {
    throw new Error(`Unexpected response format: ${text.slice(0, 200)}`);
  }
}

function clampToUS(lat: number, lng: number): boolean {
  return (
    lat >= US_BOUNDS.minLat &&
    lat <= US_BOUNDS.maxLat &&
    lng >= US_BOUNDS.minLng &&
    lng <= US_BOUNDS.maxLng
  );
}

const BirdMap: React.FC = () => {
  const mapRef = useRef<MapLibreMap | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<Marker[]>([]);
  const popupRef = useRef<Popup | null>(null);
  const sightingsRef = useRef<Record<string, Observation[]>>({});

  const [center, setCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [radiusMiles, setRadiusMiles] = useState<number>(DEFAULT_RADIUS_MILES);
  const [hotspots, setHotspots] = useState<Hotspot[]>(curatedSites);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sightings, setSightings] = useState<Record<string, Observation[]>>({});
  const [searchQuery, setSearchQuery] = useState('');

  const hasToken = useMemo(() => Boolean(EBIRD_TOKEN), []);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      center,
      zoom: DEFAULT_ZOOM,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.on('moveend', () => {
      const c = map.getCenter();
      setCenter([c.lng, c.lat]);
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPopup = useCallback(
    (hs: Hotspot) => {
      const map = mapRef.current;
      if (!map) return;
      if (popupRef.current) {
        popupRef.current.remove();
      }
      const obs = sightingsRef.current[hs.locId] || [];
      const html = `<div class="${styles.popupTitle}">${hs.locName}</div>${
        obs.length
          ? `<ul>${obs
              .slice(0, 15)
              .map(
                (o) =>
                  `<li class="${styles.popupItem}"><a class="${
                    styles.popupLink
                  }" href="${buildFieldGuideLink(o.comName)}" target="_blank" rel="noreferrer">${
                    o.comName
                  }</a>${o.howMany ? ` (${o.howMany})` : ''}${o.obsDt ? ` — ${o.obsDt}` : ''}</li>`,
              )
              .join('')}</ul>`
          : '<div>Loading sightings…</div>'
      }`;
      const popup = new maplibregl.Popup({ closeButton: true, offset: 16 })
        .setLngLat([hs.lng, hs.lat])
        .setHTML(html)
        .addTo(map);
      popupRef.current = popup;
    },
    [],
  );

  const loadSightings = useCallback(
    async (locId: string) => {
      if (!hasToken) return;
      try {
        const url = `https://api.ebird.org/v2/data/obs/${locId}/recent?maxResults=30&fmt=json`;
        const data = await fetchJson<Observation[]>(url, EBIRD_TOKEN);
        sightingsRef.current = { ...sightingsRef.current, [locId]: data };
        setSightings((prev) => ({ ...prev, [locId]: data }));
      } catch (err) {
        setError('Failed to load sightings for this hotspot.');
      }
    },
    [hasToken],
  );

  // Render markers whenever hotspots change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    hotspots.forEach((hs) => {
      const marker = new maplibregl.Marker({
        color: hs.locId === selectedId ? '#2aa366' : '#1f4a77',
      })
        .setLngLat([hs.lng, hs.lat])
        .addTo(map);
      marker.getElement().setAttribute('title', hs.locName);
      marker.getElement().addEventListener('click', () => {
        setSelectedId(hs.locId);
        map.flyTo({ center: [hs.lng, hs.lat], zoom: 10 });
        openPopup(hs);
        loadSightings(hs.locId);
      });
      markersRef.current.push(marker);
    });
  }, [hotspots, loadSightings, openPopup, selectedId]);

  const loadHotspots = async () => {
    if (!hasToken) {
      setError('Add REACT_APP_EBIRD_TOKEN to fetch hotspots.');
      return;
    }
    if (!clampToUS(center[1], center[0])) {
      setError('Search is limited to the US. Move the map over the US to search.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const distKm = radiusMiles * 1.60934;
      const url = `https://api.ebird.org/v2/data/obs/geo/recent?lat=${center[1]}&lng=${center[0]}&dist=${distKm}&hotspot=true&back=${DEFAULT_BACK_DAYS}&fmt=json`;
      const data = await fetchJson<Observation[]>(url, EBIRD_TOKEN);

      const grouped: Record<string, Observation[]> = {};
      data.forEach((obs) => {
        if (!obs.locId || obs.lat === undefined || obs.lng === undefined) return;
        if (!grouped[obs.locId]) grouped[obs.locId] = [];
        grouped[obs.locId].push(obs);
      });

      const sites: Hotspot[] = Object.keys(grouped).map((locId) => {
        const first = grouped[locId][0];
        return {
          locId,
          locName: first.locName || locId,
          lat: first.lat as number,
          lng: first.lng as number,
        };
      });

      setHotspots(sites.slice(0, 100));
      sightingsRef.current = grouped;
      setSightings(grouped);
      setSelectedId(null);
      if (popupRef.current) {
        popupRef.current.remove();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load hotspots.');
    } finally {
      setLoading(false);
    }
  };

  const selectedHotspot = selectedId ? hotspots.find((h) => h.locId === selectedId) : null;

  useEffect(() => {
    if (selectedHotspot && sightingsRef.current[selectedHotspot.locId]) {
      openPopup(selectedHotspot);
    }
  }, [openPopup, selectedHotspot, sightings]);

  const handleGeocode = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        searchQuery,
      )}&limit=1&countrycodes=us`;
      const results = await fetchJson<Array<{ lat: string; lon: string }>>(url);
      if (!results.length) {
        setError('No results found for that place in the US.');
      } else {
        const { lat, lon } = results[0];
        const latNum = Number(lat);
        const lonNum = Number(lon);
        // Clear previous hotspots/selection
        setHotspots([]);
        setSelectedId(null);
        sightingsRef.current = {};
        setSightings({});
        if (popupRef.current) {
          popupRef.current.remove();
        }
        if (mapRef.current) {
          mapRef.current.flyTo({ center: [lonNum, latNum], zoom: 9 });
        }
        setCenter([lonNum, latNum]);
      }
    } catch (err: any) {
      setError(err?.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className={styles.page}>
      <TopNavBar />
      <div className={styles.content}>
        <div className={styles.mapCard}>
          <div ref={mapContainerRef} className={styles.map} />
        </div>
        <div className={styles.panelCard}>
          <div className={styles.titleRow}>
            <h2 className={styles.heading}>Bird Map (US)</h2>
          </div>
          <div className={styles.controls}>
            <input
              type="text"
              placeholder="Search a US place..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            <button
              className={styles.button}
              type="button"
              onClick={handleGeocode}
              disabled={searching}
            >
              {searching ? 'Searching…' : 'Go'}
            </button>
            <span className={styles.groupSpacer}>Radius (mi)</span>
            <input
              type="number"
              min={1}
              max={50}
              value={radiusMiles}
              onChange={(e) => setRadiusMiles(Number(e.target.value))}
            />
            <button
              className={styles.button}
              type="button"
              onClick={loadHotspots}
              disabled={loading}
            >
              {loading ? 'Searching…' : 'Search this area'}
            </button>
          </div>
          {!hasToken && <div className={styles.error}>Add REACT_APP_EBIRD_TOKEN to load hotspots.</div>}
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.hint}>
            Pan/zoom the map anywhere in the US, then click &quot;Search this area&quot; for recent hotspots. Click a marker to see recent sightings.
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirdMap;
